import {
  ClickAwayListener,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material"
import React, { useEffect, useState } from "react"
import { styles } from "./styles"
import CloseIcon from "@mui/icons-material/Close"
import PropTypes from "prop-types"
import useApi from "modules/translator"
// import { ThreeDots } from 'react-loader-spinner'
import ReactLoading from "react-loading"
import SimpleSelect from "./SimpleSelect"
import VolumeUpIcon from "@mui/icons-material/VolumeUp"
import { useTranslation } from "react-i18next"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"

Translator.propTypes = {
  text: PropTypes.string,
  from: PropTypes.string,
  to: PropTypes.string,
  onClose: PropTypes.func,
  open: PropTypes.bool,
}

export default function Translator({
  text = "",
  from = "",
  to = "",
  onClose,
  open = false,
}) {
  const { t } = useTranslation()
  const [translation, setTranslation] = React.useState("")
  const [loading, setLoading] = React.useState(false)
  const { translate, AVALIALBE_LANGUAGES } = useApi()
  const [params, setParams] = React.useState({
    from: "en-US",
    to: "fr-FR",
  })
  const [speechSynthesisSupported, setSpeechSynthesisSupported] =
    useState(false)
  const [alternatives, setAlternatives] = React.useState([])
  const [showMore, setShowMore] = React.useState(false)
  const theme = useTheme()

  useEffect(() => {
    if ("speechSynthesis" in window) {
      setSpeechSynthesisSupported(true)
    }
  }, [])

  useEffect(() => {
    setParams({
      from: from,
      to: to,
    })
    setShowMore(false)
    setAlternatives([])
    if (!open) return
    if (text.length > 0 && open) {
      // setTranslation("Translation");
      translateText(text, {
        from: from,
        to: to,
      })
    }
  }, [open, text, from, to])

  const translateText = async (text, params) => {
    try {
      setLoading(true)
      const s = AVALIALBE_LANGUAGES.find((lang) => lang.code === params?.from)
      const t = AVALIALBE_LANGUAGES.find((lang) => lang.code === params?.to)
      console.log(`translate from ${s?.name} to ${t?.name}`)
      // setParams({ from: s.name, to: t.name })
      const response = await translate({
        q: text,
        source: s.code?.split("-")[0],
        target: t.code?.split("-")[0],
      })
      if (response) {
        setTranslation(response.translatedText)
        if (response?.alternatives?.length > 0) {
          setAlternatives(response.alternatives)
        } else {
          setAlternatives([])
        }
      }
      setLoading(false)
    } catch (error) {
      setLoading(false)
      console.log("🚀 ~ translate ~ error:", error)
      setTranslation(t("Error translating text"))
    }
  }

  const onFromLanguageChange = (e) => {
    setParams({ ...params, from: e })
    translateText(text, { from: e, to: params.to })
  }

  const onToLanguageChange = (e) => {
    setParams({ ...params, to: e })
    translateText(text, { from: params.from, to: e })
  }

  const handleSpeak = (text, lang) => {
    if (speechSynthesisSupported) {
      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = lang
      window.speechSynthesis.speak(utterance)
    }
  }

  return (
    <ClickAwayListener onClickAway={onClose}>
      <Stack sx={styles.content} className={"translator-typo-content"}>
        <IconButton
          sx={styles.closeButton}
          onClick={onClose}
          className={"translator-typo-content"}
        >
          <CloseIcon className={"translator-typo-content"} />
        </IconButton>
        <Stack sx={styles.divider} className={"translator-typo-content"} />
        <Typography
          variant="span"
          sx={styles.language}
          className={"translator-typo-content"}
        >
          {}
        </Typography>
        <SimpleSelect
          value={params?.from}
          label={`${t("from")}:`}
          onChange={onFromLanguageChange}
          data={AVALIALBE_LANGUAGES.map((lang) => ({
            value: lang.code,
            label: lang.name,
          }))}
        />
        <Stack sx={styles.translatorTypoContent}>
          {speechSynthesisSupported && (
            <IconButton
              className={"translator-typo-content"}
              sx={styles.speakButton}
              onClick={() => handleSpeak(text, params.from)}
            >
              <VolumeUpIcon fontSize="small" />
            </IconButton>
          )}
          <Typography variant="span" className={"translator-typo-content"}>
            {text}
          </Typography>
        </Stack>
        <Stack sx={styles.divider} className={"translator-typo-content"} />
        <Typography
          className={"translator-typo-content"}
          variant="span"
          sx={styles.language}
        >
          {}
        </Typography>
        <SimpleSelect
          value={params?.to}
          label={`${t("to")}:`}
          onChange={onToLanguageChange}
          data={AVALIALBE_LANGUAGES.map((lang) => ({
            value: lang.code,
            label: lang.name,
          }))}
        />
        {loading ? (
          <ReactLoading type={"bubbles"} color={theme.palette.primary.main} />
        ) : (
          <Stack sx={styles.translatorTypoContent}>
            {speechSynthesisSupported && (
              <IconButton
                className={"translator-typo-content"}
                sx={styles.speakButton}
                onClick={() => handleSpeak(translation, params.to)}
              >
                <VolumeUpIcon fontSize="small" />
              </IconButton>
            )}
            <Typography
              className={"translator-typo-content"}
              variant="span"
              sx={styles.translationText}
            >
              {translation}
            </Typography>
          </Stack>
        )}
        {!loading && alternatives?.length > 0 && (
          <Typography
            onClick={() => setShowMore(!showMore)}
            sx={[styles.more, styles.clickable]}
            variant="span"
          >
            {t("more_translations")}
            {showMore ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </Typography>
        )}
        {!loading && alternatives?.length > 0 && showMore && (
          <Stack sx={styles.moreOutput}>
            <Stack>
              {alternatives.map((alt, index) => (
                <Stack key={index} sx={styles.moreItem}>
                  <Typography
                    variant="span"
                    sx={styles.more}
                    mt={index === 0 ? 2 : 0}
                    py={0.5}
                  >
                    {alt}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        )}
      </Stack>
    </ClickAwayListener>
  )
}
